/**
 * <AiExplain /> — drop-in panel that renders an "Explain with AI" button
 * and (on click) a one-line summary + 1-2 sentence detail blurb sourced
 * from `/api/ai/explain`.
 *
 * Three states:
 *   - **idle**: shows the button only. Lazy by design — we don't burn
 *     tokens until the user actually wants the explanation.
 *   - **loading**: button replaced with a Sparkles icon + "Thinking…"
 *     hint. Latency is typically 1-2s.
 *   - **ready / error**: panel rendered with summary, details, model
 *     attribution, and a "regenerate" button.
 *
 * The panel intentionally lives *inline* with the row that triggered it
 * (no modal, no popover) so the user keeps full context of what was
 * explained. The variant prop tunes density: "panel" is the default
 * (full padding, used in expanded sections); "compact" is a tighter
 * alternative for inline use in narrow tables.
 */
import { useMemo } from "react";
import { Sparkles, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";
import { useAiExplain, type AiExplainKind } from "@/stores/ai-explain";

interface AiExplainProps {
  kind:    AiExplainKind;
  context: Record<string, unknown>;
  /** "panel" = bordered card, "compact" = inline text block. */
  variant?: "panel" | "compact";
  /** Optional override for the trigger button label. */
  label?:  string;
}

export function AiExplain({
  kind,
  context,
  variant = "panel",
  label = "Explain with AI",
}: AiExplainProps) {
  const load   = useAiExplain((s) => s.load);
  const keyFor = useAiExplain((s) => s.keyFor);
  // Memoize the key so the selector below doesn't re-compute on every render
  // (stableHash is cheap but JSON.stringify on a large alert payload isn't).
  const key   = useMemo(() => keyFor(kind, context), [kind, context, keyFor]);
  const entry = useAiExplain((s) => s.byKey[key]);

  const onClick = () => void load(kind, context);
  const onRegenerate = () => void load(kind, context, { force: true });

  // Idle — render just the trigger button.
  if (!entry) {
    return (
      <Button
        size="sm"
        variant="ghost"
        onClick={onClick}
        className={cn("gap-1.5", variant === "compact" && "h-7 px-2 text-[11px]")}
      >
        <Sparkles size={12} className="text-violet" />
        {label}
      </Button>
    );
  }

  // Loading — same footprint as the button so the row doesn't reflow.
  if (entry.status === "loading") {
    return (
      <div
        className={cn(
          "inline-flex items-center gap-1.5 text-[11px] text-fg-mute",
          variant === "compact" ? "h-7 px-2" : "px-3 py-2",
        )}
      >
        <Sparkles size={12} className="animate-pulse text-violet" />
        Thinking…
      </div>
    );
  }

  // Error — small hint + retry.
  if (entry.status === "error") {
    return (
      <div
        className={cn(
          "flex items-center justify-between gap-3 rounded-md border border-rose/30 bg-rose/5 text-[11px] text-rose",
          variant === "compact" ? "px-2 py-1" : "px-3 py-2",
        )}
      >
        <span>{entry.error || "AI explanations failed"}</span>
        <button
          type="button"
          onClick={onRegenerate}
          className="inline-flex items-center gap-1 text-rose hover:underline"
        >
          <RefreshCw size={10} /> retry
        </button>
      </div>
    );
  }

  // Ready — render the explanation. The data shape is locked by the shim.
  const { summary, details, model, elapsedMs } = entry.data!;
  return (
    <div
      className={cn(
        "rounded-md border border-violet/30 bg-violet/5",
        variant === "compact" ? "px-2 py-1.5" : "px-3 py-2.5",
      )}
    >
      <div className="flex items-start gap-2">
        <Sparkles size={12} className="mt-0.5 shrink-0 text-violet" />
        <div className="min-w-0 flex-1">
          <div className="text-[12px] font-semibold text-fg">{summary}</div>
          {details ? (
            <div className="mt-0.5 text-[11px] leading-relaxed text-fg-dim">{details}</div>
          ) : null}
          <div className="mt-1.5 flex items-center justify-between text-[10px] text-fg-mute">
            <span className="font-mono">{model} · {elapsedMs}ms</span>
            <button
              type="button"
              onClick={onRegenerate}
              className="inline-flex items-center gap-1 hover:text-fg-dim"
              title="Regenerate"
            >
              <RefreshCw size={9} /> regenerate
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
